import { Injectable, inject } from '@angular/core';
import { clone } from 'lodash-es';
import { assert } from '../../../../core/assert/assert';
import {
  GapAttribute,
  SortedIndexAttribute,
} from '../../../../core/constants-repository/sortable-rows.constants';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { SortableEntriesDefaultAnimationDuration } from '../../../../core/services/features/sortable-elements/sortable-elements.constants';
import { SortableEntriesService } from '../../../../core/services/features/sortable-elements/sortable-entries.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { TodoPlannerHttpService } from '../../../../core/state/todo-planner/todo-planner-http.service';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { TodoPlannerService } from '../../services/todo-planner.service';

@Injectable({
  providedIn: 'root',
})
export class TodoEntryService {
  // DI
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoPlannerHttpService = inject(TodoPlannerHttpService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly sortableEntriesService = inject(SortableEntriesService);

  public static cloneTodoModel(orig: TodoEntryModel, removeComponentRef = true): TodoEntryModel {
    const clonedModel = clone(orig);
    if (removeComponentRef) {
      clonedModel.componentRef = null;
    }
    return clonedModel;
  }

  /**
   * Create a to-do component and send the new to-do to the server afterward.
   **/
  public createTodo(day: Date, value: string, userId: number): TodoEntryModel {
    this.todoPlannerService.activeAddTodoEntryLaneId.set(-1);
    const todoEntriesInLane =
      this.todoPlannerService.todoPlannerModel.variableIntervalTodoEntryModels.get(
        DateTimeHelper.format(day),
      );

    const newTodoEntryModel = new TodoEntryModel();
    newTodoEntryModel.start = day;
    newTodoEntryModel.end = day;
    newTodoEntryModel.name = value;
    newTodoEntryModel.color = '#A0A0A0';
    newTodoEntryModel.userId = userId;
    newTodoEntryModel.order = todoEntriesInLane.length;
    return newTodoEntryModel;
  }

  public getTodoEntryPositionInLane(todoEntryModel: TodoEntryModel): number {
    const todoEntryModels = this.todoPlannerService.getTodoEntryModels(todoEntryModel);
    const start = this.getCorrectContainerDate(todoEntryModel.start);
    const laneTodoEntries = todoEntryModels.get(DateTimeHelper.format(start));

    const index = laneTodoEntries.findIndex((p) => p.id === todoEntryModel.id);
    assert(index >= 0, 'To-do entry was not found.');

    return index;
  }

  public updateSortedIndices(originalLaneDate: Date, alteredTodoEntry: TodoEntryModel): void {
    // If we update the to-do entry start and end dates to be outside the visible view, there is no component ref and hence
    // don't update the sorted indices.
    if (!alteredTodoEntry.componentRef) {
      return;
    }

    const laneDate = DateTimeHelper.format(this.getCorrectContainerDate(originalLaneDate));
    const todoEntryModels = this.todoPlannerService.getTodoEntryModels(alteredTodoEntry);
    const laneTodoEntries = todoEntryModels.get(laneDate);

    laneTodoEntries.forEach((entry: TodoEntryModel, index) => {
      entry.order = index;
      this.shiveRenderService.setAttribute(
        entry.componentRef.instance.todoEntryWrapper().nativeElement,
        SortedIndexAttribute,
        index.toString(),
      );
    });
  }

  public resetDataAttributesAndTransform(toBeInsertedTodoEntry: TodoEntryModel): void {
    const todoEntryModels =
      this.todoPlannerService.todoPlannerModel.variableIntervalTodoEntryModels;
    const laneEntries = todoEntryModels.get(DateTimeHelper.format(toBeInsertedTodoEntry.start));

    const elements = laneEntries.map(
      (entry: TodoEntryModel) => entry.componentRef.instance.todoEntryWrapper().nativeElement,
    ) as HTMLElement[];

    // Add the "Add to-do" button to the result list.
    const htmlContainer =
      this.todoPlannerService.getTodoEntryWrapper(toBeInsertedTodoEntry).elementRef.nativeElement;
    const addTodoBtn = htmlContainer.nextElementSibling as HTMLElement;
    elements.push(addTodoBtn);

    this.moveElementsUp(elements, 0);
  }

  public moveElementsUp(
    elements: HTMLElement[],
    animationDuration = SortableEntriesDefaultAnimationDuration,
  ): void {
    elements.forEach((entry: HTMLElement, index: number) => {
      this.shiveRenderService.removeAttribute(entry, GapAttribute);
      this.sortableEntriesService.updateAndAnimateEntries(entry, index, 0, true, animationDuration); // No animation
    });
  }

  public getCorrectContainerDate(start: Date): Date {
    let containerStart = this.todoPlannerService.todoPlannerModel.plannerStart;
    containerStart = DateTimeHelper.isBefore(start, containerStart) ? containerStart : start;

    return containerStart;
  }

  public isOutsideOfView(todoEntryModel: TodoEntryModel): boolean {
    const plannerStart = this.todoPlannerService.todoPlannerModel.plannerStart;
    const plannerEnd = this.todoPlannerService.todoPlannerModel.plannerEnd;

    const start = todoEntryModel.start;
    const end = todoEntryModel.end;

    const isOutsideLeft =
      DateTimeHelper.isBefore(start, plannerStart) && DateTimeHelper.isBefore(end, plannerStart);
    const isOutsideRight =
      DateTimeHelper.isAfter(start, plannerEnd) && DateTimeHelper.isAfter(end, plannerEnd);

    return isOutsideLeft || isOutsideRight;
  }

  public sendToServer(laneDate: Date): void {
    const todoPlannerModel = this.todoPlannerService.todoPlannerModel;
    const todoEntryModels = todoPlannerModel.variableIntervalTodoEntryModels;
    const lane =
      todoEntryModels
        .get(DateTimeHelper.format(laneDate))
        .map((e) => TodoEntryService.cloneTodoModel(e)) ?? [];

    this.todoPlannerHttpService.updateMultipleTodoEntries(lane);
  }

  public sendMultipleLanesToServer(laneDates: Date[]): void {
    const todoPlannerModel = this.todoPlannerService.todoPlannerModel;
    const todoEntryModels = todoPlannerModel.variableIntervalTodoEntryModels;

    const allEntries = laneDates.flatMap(
      (laneDate) =>
        todoEntryModels
          .get(DateTimeHelper.format(laneDate))
          .map((e) => TodoEntryService.cloneTodoModel(e)) ?? [],
    );

    this.todoPlannerHttpService.updateMultipleTodoEntries(allEntries);
  }
}
